// Lab-side "Request Lab" page.
//
// Mirrors the doctor workflow in Doctors/RequsetLabDialog.js but lives on the
// Investigations menu so a lab supervisor can initiate a new request without
// going through a clinician. Same POST /requestLab payload, so the backend's
// existing billing logic kicks in:
//
//   • amount  > 0  → lab_charge row with status='pending'. The request sits in
//                    Accounts until payment is processed; only then is a
//                    corresponding lab_request row created (server.js inserts
//                    the worklist row once a PaymentProcess settles the charge),
//                    and the patient surfaces in Lab > My Patients.
//   • amount == 0  → request is treated as free / fully insured: both
//                    lab_charge and lab_request rows are written immediately,
//                    so the patient appears in My Patients on the next refresh.
//
// We never bypass that flow — the lab supervisor is just a different
// originator of an otherwise identical request.

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, TextField, InputAdornment, Button, IconButton,
  Avatar, Chip, Divider, Stack, Grid, Card, CardActionArea, CardContent,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  ToggleButton, ToggleButtonGroup, CircularProgress, Snackbar, Alert,
  Tooltip, useTheme, Autocomplete,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Science as ScienceIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Send as SendIcon,
  Person as PersonIcon,
  ArrowBack as ArrowBackIcon,
  LocalHospital as HospitalIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as HourglassIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  CalendarToday as CalendarIcon,
  MedicalServices as DoctorIcon,
  DirectionsWalk as WalkInIcon,
} from '@mui/icons-material';
import axios from 'axios';
import moment from 'moment';
import Swal from 'sweetalert2';

import {
  loadAllPatientsToday, loadAllInPatients, loadLabdb, requestLab,
  loadRequestedLabsInpatients, onboardedDoctorsListUrl,
} from '../BaseURL';
import { formatWithOutSom } from '../Message';
import { computeAge } from '../../utils/age';

const FILTERS = [
  { value: 'outpatient', label: 'Outpatients' },
  { value: 'inpatient',  label: 'Inpatients'  },
];

export default function RequestLab() {
  // ── Theme tokens — same surface palette as Lab > My Patients so the page
  // doesn't feel like a stranger when an admin tabs between them, and dark
  // mode renders pure-black backgrounds instead of bleeding white pastels.
  const muiTheme = useTheme();
  const isDark = muiTheme.palette.mode === 'dark';
  const t = useMemo(() => ({
    isDark,
    pageBg:       isDark ? '#000000' : '#F4F6FA',
    surface:      isDark ? '#0E0E14' : '#FFFFFF',
    surfaceAlt:   isDark ? '#14141C' : '#F8FAFC',
    fieldBg:      isDark ? '#14141C' : '#FFFFFF',
    borderSubtle: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,23,42,0.08)',
    borderStrong: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(15,23,42,0.14)',
    text:         isDark ? '#FFFFFF' : '#0F172A',
    textMuted:    isDark ? 'rgba(255,255,255,0.62)' : 'rgba(15,23,42,0.6)',
    textDim:      isDark ? 'rgba(255,255,255,0.42)' : 'rgba(15,23,42,0.4)',
    rowHover:     isDark ? 'rgba(255,255,255,0.04)' : 'rgba(15,23,42,0.03)',
    accent: {
      teal:    '#11998e',
      green:   '#22c55e',
      emerald: '#10b981',
      blue:    '#3b82f6',
      amber:   '#f59e0b',
      danger:  '#ef4444',
    },
  }), [isDark]);

  const today = moment().format('YYYY-MM-DD');

  // ── Patient list state ────────────────────────────────────────────────
  const [filter, setFilter]               = useState('outpatient');
  const [patients, setPatients]           = useState([]);
  const [patientsLoading, setPatientsLoading] = useState(false);
  const [patientSearch, setPatientSearch] = useState('');

  // ── Selected patient + lab-request workspace ──────────────────────────
  // Requests are always recorded against today's date — the lab supervisor
  // can't back-date or forward-date a request, so there's no picker.
  const [selectedPatient, setSelectedPatient] = useState(null);

  const [labs, setLabs]               = useState([]);
  const [labsLoading, setLabsLoading] = useState(false);
  const [labSearch, setLabSearch]     = useState('');
  const [requestingLabId, setRequestingLabId] = useState(null);

  const [existingRequests, setExistingRequests] = useState([]); // server-side: today's requests
  const [sessionRequests, setSessionRequests]   = useState([]); // local: requested this visit

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [currentUser, setCurrentUser] = useState('');

  // Referring / requesting clinician — same shape used on the Medical
  // Records request workspace. Every request fired from this page is
  // tagged with this doctor on `doc_name` (or "Walk-in" when the patient
  // arrived without a referral) so the commission report keys off it.
  const WALK_IN = useMemo(() => ({ id: '__walkin__', name: 'Walk-in', _walkin: true }), []);
  const [doctorOptions, setDoctorOptions] = useState([WALK_IN]);
  const [doctorsLoading, setDoctorsLoading] = useState(false);
  const [referringDoctor, setReferringDoctor] = useState(null);

  // ── Pull logged-in username so the backend can attribute the request to
  //    the lab supervisor (otherwise it logs as anonymous).
  useEffect(() => {
    try {
      const raw = localStorage.getItem('userInfo');
      if (raw) {
        const u = JSON.parse(raw);
        setCurrentUser(u?.name || u?.username || '');
      }
    } catch (_) { /* non-fatal */ }
  }, []);

  // ── Onboarded doctors loader ─────────────────────────────────────────
  // Pinned "Walk-in" stays on top of the list so it's a one-click pick
  // for the common no-referral case.
  const loadDoctors = async () => {
    setDoctorsLoading(true);
    try {
      const res = await axios.get(onboardedDoctorsListUrl, { params: { status: 'active' }, timeout: 12000 });
      const rows = res.data?.success ? (res.data.results || []) : [];
      setDoctorOptions([WALK_IN, ...rows]);
    } catch (err) {
      console.error('Failed to load onboarded doctors:', err.message);
      setDoctorOptions([WALK_IN]);
    } finally {
      setDoctorsLoading(false);
    }
  };
  useEffect(() => { loadDoctors(); /* eslint-disable-next-line */ }, []);

  // ── Patient list loader ───────────────────────────────────────────────
  const loadPatients = async () => {
    setPatientsLoading(true);
    try {
      const endpoint = filter === 'inpatient' ? loadAllInPatients : loadAllPatientsToday;
      const res = await axios.get(endpoint);
      if (res.data?.success) {
        setPatients(Array.isArray(res.data.results) ? res.data.results : []);
      } else {
        setPatients([]);
      }
    } catch (err) {
      console.error('Failed to load patients:', err);
      setPatients([]);
    } finally {
      setPatientsLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedPatient) loadPatients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, selectedPatient]);

  // ── Lab catalogue + already-requested loaders for the selected patient ─
  const loadLabsForPatient = async (patient) => {
    setLabsLoading(true);
    try {
      const res = await axios.get(loadLabdb, { params: { insurance: patient.insurance } });
      if (res.data?.success) setLabs(res.data.results || []);
      else setLabs([]);
    } catch (err) {
      console.error('Failed to load lab catalogue:', err);
      setLabs([]);
    } finally {
      setLabsLoading(false);
    }
  };

  const loadExistingRequests = async (patient) => {
    try {
      const res = await axios.get(loadRequestedLabsInpatients, {
        params: { patientId: patient.id, dat: today, currentDate: today },
      });
      if (res.data?.success) setExistingRequests(res.data.results || []);
    } catch (err) {
      // Non-fatal: the duplicate check just won't fire.
      setExistingRequests([]);
    }
  };

  const handleSelectPatient = (patient) => {
    setSelectedPatient(patient);
    setLabSearch('');
    setSessionRequests([]);
    loadLabsForPatient(patient);
    loadExistingRequests(patient);
  };

  const handleBackToList = () => {
    setSelectedPatient(null);
    setLabs([]);
    setExistingRequests([]);
    setSessionRequests([]);
    setLabSearch('');
  };

  // ── Request a single lab (identical payload to the doctor flow) ───────
  const handleRequestLab = async (lab, labRowKey) => {
    if (!selectedPatient) return;

    // Referring doctor must be chosen first — even walk-ins pick the
    // pinned "Walk-in" option explicitly so the doc_name on every row
    // is deterministic for the commission report.
    if (!referringDoctor) {
      setSnackbar({
        open: true, severity: 'error',
        message: 'Select a referring doctor (or "Walk-in") before requesting.',
      });
      return;
    }

    // Duplicate guard — same lab, same day → ask before re-submitting so the
    // lab supervisor doesn't accidentally double-bill an insured patient.
    const dupServer = existingRequests.some(r => r.lab === lab.name && r.date === today);
    const dupSession = sessionRequests.some(r => r.labName === lab.name);
    if (dupServer || dupSession) {
      const result = await Swal.fire({
        title: 'Already requested today',
        text: `${lab.name} has already been requested for ${selectedPatient.fullName} today. Request again?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, request again',
        cancelButtonText: 'Cancel',
        confirmButtonColor: t.accent.teal,
        cancelButtonColor: t.accent.danger,
      });
      if (!result.isConfirmed) return;
    }

    setRequestingLabId(labRowKey);
    try {
      const res = await axios.post(requestLab, {
        patientId: selectedPatient.id,
        dat: today,
        patientInsurance: selectedPatient.insurance,
        labName: lab.name,
        amount: lab.amount,
        base_cost: lab.base_cost,
        code: lab.code,
        currentUser,
        type: 'Lab',
        referringDoctor: referringDoctor.name,    // doc_name on every row
      });

      if (res.data?.success) {
        // Backend stamps a barcode-ready tracking ID (LAB-xxxxxx) on every
        // request, even free ones. We surface it on the toast and the
        // session strip so the supervisor reads the same string that will
        // print on the slip.
        const reqNo = res.data?.reqNo || null;
        setSessionRequests(prev => [
          ...prev,
          {
            labName: lab.name,
            amount: lab.amount,
            insured: Number(lab.amount) === 0,
            reqNo,
            at: new Date().toISOString(),
          },
        ]);
        setSnackbar({
          open: true,
          severity: 'success',
          message: `${reqNo ? reqNo + ' · ' : ''}${lab.name} ${
            Number(lab.amount) === 0
              ? 'requested — patient will appear in My Patients shortly.'
              : 'sent to Accounts for payment.'
          }`,
        });
        // Refresh the server-side duplicate list so consecutive clicks see it.
        loadExistingRequests(selectedPatient);
      } else {
        setSnackbar({
          open: true,
          severity: 'error',
          message: res.data?.message || `Could not request ${lab.name}.`,
        });
      }
    } catch (err) {
      console.error('requestLab failed:', err);
      setSnackbar({
        open: true,
        severity: 'error',
        message: err.response?.data?.message || `Could not request ${lab.name}.`,
      });
    } finally {
      setRequestingLabId(null);
    }
  };

  // ── Derived: filtered patient + lab lists ─────────────────────────────
  const filteredPatients = useMemo(() => {
    const q = patientSearch.trim().toLowerCase();
    if (!q) return patients;
    return patients.filter(p =>
      String(p.id || '').toLowerCase().includes(q) ||
      String(p.fullName || '').toLowerCase().includes(q) ||
      String(p.tel || '').toLowerCase().includes(q)
    );
  }, [patients, patientSearch]);

  const filteredLabs = useMemo(() => {
    const q = labSearch.trim().toLowerCase();
    if (!q) return labs;
    return labs.filter(l => String(l.name || '').toLowerCase().includes(q));
  }, [labs, labSearch]);

  // ────────────────────────────────────────────────────────────────────
  // Render
  // ────────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: t.pageBg, p: { xs: 1.5, md: 3 } }}>
      {/* ── Page header ─────────────────────────────────────────────── */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, md: 2 },
          mb: 2,
          borderRadius: 2,
          bgcolor: t.surface,
          border: `1px solid ${t.borderSubtle}`,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <Avatar sx={{
          bgcolor: alpha(t.accent.teal, 0.16),
          color: t.accent.teal,
          width: 44, height: 44,
          border: `1px solid ${alpha(t.accent.teal, 0.32)}`,
        }}>
          <ScienceIcon />
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{
            fontSize: '0.7rem', fontWeight: 800, letterSpacing: '1.4px',
            textTransform: 'uppercase', color: t.accent.teal, lineHeight: 1,
          }}>
            Investigations
          </Typography>
          <Typography sx={{
            fontSize: { xs: '1.05rem', md: '1.2rem' }, fontWeight: 700,
            color: t.text, mt: 0.25,
          }}>
            Request Lab
            <Typography component="span" sx={{
              fontSize: '0.78rem', fontWeight: 500, color: t.textMuted, ml: 1.25,
            }}>
              Send a lab order to Accounts on behalf of a patient
            </Typography>
          </Typography>
        </Box>

        {selectedPatient && (
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBackToList}
            sx={{
              textTransform: 'none', fontWeight: 600,
              color: t.textMuted, borderColor: t.borderStrong,
              '&:hover': { bgcolor: t.rowHover, borderColor: t.borderStrong },
            }}
            variant="outlined"
            size="small"
          >
            Back to patient list
          </Button>
        )}
      </Paper>

      {!selectedPatient ? (
        <PatientPicker
          t={t}
          filter={filter}
          setFilter={setFilter}
          patientSearch={patientSearch}
          setPatientSearch={setPatientSearch}
          filteredPatients={filteredPatients}
          patientsLoading={patientsLoading}
          onRefresh={loadPatients}
          onSelect={handleSelectPatient}
        />
      ) : (
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 320px' } }}>
          <Box>
            <PatientHeader t={t} patient={selectedPatient} />

            {/* Referring-doctor selector — gates every request below.
                Picks from onboarded doctors with a pinned "Walk-in" for
                no-referral patients. Selection becomes doc_name on the
                lab_charge / lab_request / investigation rows so the
                Medical Records commission report can attribute revenue. */}
            <ReferringDoctorBar
              t={t}
              accent={t.accent.teal}
              options={doctorOptions}
              loading={doctorsLoading}
              value={referringDoctor}
              onChange={setReferringDoctor}
              onRefresh={loadDoctors}
            />

            <LabCatalogue
              t={t}
              labs={filteredLabs}
              labSearch={labSearch}
              setLabSearch={setLabSearch}
              today={today}
              labsLoading={labsLoading}
              requestingLabId={requestingLabId}
              onRequest={handleRequestLab}
              sessionRequests={sessionRequests}
              existingRequests={existingRequests}
            />
          </Box>
          {/* Sticky, self-scrolling right rail. Lets the lab supervisor
              page through long session/on-file lists without losing the
              patient header, the doctor bar, or the catalogue. */}
          <Box
            sx={{
              position: { lg: 'sticky' },
              top: { lg: 16 },
              alignSelf: 'flex-start',
              maxHeight: { lg: 'calc(100vh - 32px)' },
              overflowY: { lg: 'auto' },
              pr: { lg: 0.5 },
              '&::-webkit-scrollbar': { width: 8 },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: t.borderStrong,
                borderRadius: 4,
              },
              '&::-webkit-scrollbar-thumb:hover': { backgroundColor: t.textMuted },
              scrollbarWidth: 'thin',
              scrollbarColor: `${t.borderStrong} transparent`,
            }}
          >
            <RequestSidebar
              t={t}
              sessionRequests={sessionRequests}
              existingRequests={existingRequests}
              currentUser={currentUser}
            />
          </Box>
        </Box>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          sx={{ fontWeight: 600 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// ──────────────────────────────────────────────────────────────────────
//  Patient picker — shown before a patient is selected
// ──────────────────────────────────────────────────────────────────────
function PatientPicker({
  t, filter, setFilter, patientSearch, setPatientSearch,
  filteredPatients, patientsLoading, onRefresh, onSelect,
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1.5, md: 2 },
        borderRadius: 2,
        bgcolor: t.surface,
        border: `1px solid ${t.borderSubtle}`,
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        alignItems={{ md: 'center' }}
        sx={{ mb: 2 }}
      >
        <TextField
          fullWidth
          size="small"
          value={patientSearch}
          onChange={(e) => setPatientSearch(e.target.value)}
          placeholder="Search by name, patient ID, or phone…"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: t.textMuted, fontSize: 18 }} />
              </InputAdornment>
            ),
            endAdornment: patientSearch && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setPatientSearch('')}>
                  <ClearIcon fontSize="small" sx={{ color: t.textMuted }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 1.5, bgcolor: t.fieldBg,
              '& fieldset': { borderColor: t.borderSubtle },
            },
          }}
        />

        <ToggleButtonGroup
          exclusive
          size="small"
          value={filter}
          onChange={(_, v) => v && setFilter(v)}
          sx={{
            '& .MuiToggleButton-root': {
              textTransform: 'none', fontWeight: 600, fontSize: '0.82rem',
              color: t.textMuted, borderColor: t.borderSubtle, px: 1.75,
              '&.Mui-selected': {
                bgcolor: alpha(t.accent.teal, 0.16),
                color: t.accent.teal,
                borderColor: alpha(t.accent.teal, 0.4),
                '&:hover': { bgcolor: alpha(t.accent.teal, 0.22) },
              },
            },
          }}
        >
          {FILTERS.map(f => (
            <ToggleButton key={f.value} value={f.value}>{f.label}</ToggleButton>
          ))}
        </ToggleButtonGroup>

        <Tooltip title="Refresh list">
          <IconButton
            onClick={onRefresh}
            size="small"
            sx={{ border: `1px solid ${t.borderSubtle}`, color: t.textMuted }}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      <Divider sx={{ borderColor: t.borderSubtle, mb: 1.5 }} />

      {patientsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={28} sx={{ color: t.accent.teal }} />
        </Box>
      ) : filteredPatients.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <PersonIcon sx={{ fontSize: 48, color: t.textDim, mb: 1 }} />
          <Typography sx={{ color: t.textMuted, fontWeight: 600 }}>
            {patientSearch ? 'No patients match that search.' : 'No active patients to show.'}
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={1.25}>
          {filteredPatients.map((p) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={p.id}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 2,
                  border: `1px solid ${t.borderSubtle}`,
                  bgcolor: t.surfaceAlt,
                  transition: 'border-color 120ms ease, transform 120ms ease',
                  '&:hover': {
                    borderColor: alpha(t.accent.teal, 0.5),
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                <CardActionArea onClick={() => onSelect(p)} sx={{ p: 1.25 }}>
                  <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                    <Stack direction="row" alignItems="center" spacing={1.25}>
                      <Avatar sx={{
                        bgcolor: p.state === 'inPatient'
                          ? alpha(t.accent.blue, 0.18)
                          : alpha(t.accent.teal, 0.16),
                        color: p.state === 'inPatient' ? t.accent.blue : t.accent.teal,
                        width: 40, height: 40, fontWeight: 700,
                      }}>
                        {String(p.fullName || '?').trim().charAt(0).toUpperCase()}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{
                          fontWeight: 700, color: t.text, fontSize: '0.92rem',
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        }}>
                          {p.fullName || '—'}
                        </Typography>
                        <Typography sx={{ color: t.textMuted, fontSize: '0.72rem' }}>
                          {p.id} · {p.gender || '—'} · {computeAge(p.dob)?.label || (p.age ? `${p.age}y` : '—')}
                        </Typography>
                      </Box>
                    </Stack>
                    <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap' }}>
                      <Chip
                        size="small"
                        label={p.insurance || 'Cash'}
                        sx={{
                          height: 20, fontSize: '0.65rem', fontWeight: 700,
                          bgcolor: alpha(t.accent.amber, 0.16),
                          color: t.accent.amber,
                        }}
                      />
                      <Chip
                        size="small"
                        label={p.state === 'inPatient' ? 'Inpatient' : 'Outpatient'}
                        sx={{
                          height: 20, fontSize: '0.65rem', fontWeight: 700,
                          bgcolor: p.state === 'inPatient'
                            ? alpha(t.accent.blue, 0.16)
                            : alpha(t.accent.teal, 0.16),
                          color: p.state === 'inPatient' ? t.accent.blue : t.accent.teal,
                        }}
                      />
                    </Stack>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Paper>
  );
}

// ──────────────────────────────────────────────────────────────────────
//  Selected-patient header
// ──────────────────────────────────────────────────────────────────────
// ──────────────────────────────────────────────────────────────────────
//  Referring-doctor selector — onboarded doctors + a pinned "Walk-in".
//  Mirrors the same control on the Medical Records request workspace so
//  the doc_name attribution is consistent across the three request flows.
// ──────────────────────────────────────────────────────────────────────
function ReferringDoctorBar({ t, accent, options, loading, value, onChange, onRefresh }) {
  const isWalkIn = value && value._walkin;
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1, md: 1.25 },
        mb: 2,
        borderRadius: 2,
        bgcolor: t.surface,
        // Border outlines in the accent until a doctor is picked, so the
        // gate is impossible to miss.
        border: `1px solid ${value ? t.borderSubtle : alpha(accent, 0.45)}`,
        display: 'flex', alignItems: 'center', gap: 1.25,
      }}
    >
      <Avatar sx={{
        bgcolor: alpha(accent, 0.16),
        color: accent,
        width: 36, height: 36,
        border: `1px solid ${alpha(accent, 0.32)}`,
      }}>
        {isWalkIn ? <WalkInIcon sx={{ fontSize: 20 }} /> : <DoctorIcon sx={{ fontSize: 20 }} />}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{
          fontSize: '0.66rem', fontWeight: 800, letterSpacing: '1.2px',
          textTransform: 'uppercase', color: t.textMuted, lineHeight: 1, mb: 0.5,
        }}>
          Requesting on behalf of *
        </Typography>
        <Autocomplete
          size="small"
          options={options}
          value={value}
          loading={loading}
          onChange={(_, v) => onChange(v)}
          getOptionLabel={(o) => (o?._walkin ? 'Walk-in' : (o?.name || ''))}
          isOptionEqualToValue={(o, v) => (o?.id || o?.name) === (v?.id || v?.name)}
          renderOption={(props, option) => (
            <Box component="li" {...props} key={option.id || option.name} sx={{
              display: 'flex', alignItems: 'center', gap: 1,
              borderBottom: option._walkin ? `1px dashed ${t.borderSubtle}` : 'none',
            }}>
              {option._walkin
                ? <WalkInIcon sx={{ fontSize: 18, color: accent }} />
                : <DoctorIcon  sx={{ fontSize: 18, color: t.textMuted }} />}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: option._walkin ? 800 : 600, color: t.text, fontSize: '0.86rem' }}>
                  {option._walkin ? 'Walk-in (no referring doctor)' : `Dr. ${option.name}`}
                </Typography>
                {!option._walkin && (option.specialization || option.facility_name) && (
                  <Typography sx={{ fontSize: '0.72rem', color: t.textMuted }}>
                    {[option.specialization, option.facility_name].filter(Boolean).join(' · ')}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Search onboarded doctors or pick Walk-in…"
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1.5, bgcolor: t.fieldBg, color: t.text,
                  '& fieldset': { borderColor: t.borderSubtle },
                },
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress size={14} sx={{ color: accent, mr: 1 }} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          sx={{ '& .MuiAutocomplete-popupIndicator': { color: t.textMuted } }}
        />
      </Box>
      {value && (
        <Chip
          size="small"
          icon={isWalkIn ? <WalkInIcon sx={{ fontSize: 14 }} /> : <DoctorIcon sx={{ fontSize: 14 }} />}
          label={isWalkIn ? 'Walk-in' : `Dr. ${value.name}`}
          sx={{
            height: 26, fontWeight: 700, fontSize: '0.72rem',
            bgcolor: alpha(accent, 0.16), color: accent,
            border: `1px solid ${alpha(accent, 0.32)}`,
          }}
        />
      )}
      <Tooltip title="Refresh doctor list">
        <span>
          <IconButton size="small" onClick={onRefresh}
            sx={{ color: t.textMuted, border: `1px solid ${t.borderSubtle}` }}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Paper>
  );
}

function PatientHeader({ t, patient }) {
  const ageInfo = computeAge(patient.dob);
  const ageLabel = ageInfo?.label || (patient.age ? `${patient.age}y` : '—');
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1.5, md: 2 },
        mb: 2,
        borderRadius: 2,
        bgcolor: t.surface,
        border: `1px solid ${t.borderSubtle}`,
        display: 'flex', alignItems: 'center', gap: 1.5,
      }}
    >
      <Avatar sx={{
        bgcolor: alpha(t.accent.teal, 0.16),
        color: t.accent.teal,
        width: 48, height: 48, fontWeight: 700, fontSize: '1.1rem',
        border: `1px solid ${alpha(t.accent.teal, 0.32)}`,
      }}>
        {String(patient.fullName || '?').trim().charAt(0).toUpperCase()}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, color: t.text, fontSize: '1.05rem' }}>
          {patient.fullName}
          <Typography component="span" sx={{ ml: 1, fontWeight: 500, color: t.textMuted, fontSize: '0.85rem' }}>
            · ID {patient.id}
          </Typography>
        </Typography>
        <Stack direction="row" spacing={0.75} sx={{ mt: 0.5, flexWrap: 'wrap' }}>
          <Chip size="small" label={patient.gender || '—'}     sx={chipSx(t, t.accent.blue)} />
          <Chip size="small" label={ageLabel}                  sx={chipSx(t, t.accent.emerald)} />
          <Chip size="small" label={patient.insurance || 'Cash'} sx={chipSx(t, t.accent.amber)} />
          <Chip
            size="small"
            label={patient.state === 'inPatient' ? 'Inpatient' : 'Outpatient'}
            sx={chipSx(t, patient.state === 'inPatient' ? t.accent.blue : t.accent.teal)}
          />
        </Stack>
      </Box>
    </Paper>
  );
}

function chipSx(t, color) {
  return {
    height: 22, fontSize: '0.7rem', fontWeight: 700,
    bgcolor: alpha(color, 0.16), color,
    border: `1px solid ${alpha(color, 0.28)}`,
  };
}

// ──────────────────────────────────────────────────────────────────────
//  Lab catalogue + per-row Request button
// ──────────────────────────────────────────────────────────────────────
function LabCatalogue({
  t, labs, labSearch, setLabSearch, today,
  labsLoading, requestingLabId, onRequest, sessionRequests, existingRequests,
}) {
  const isRequested = (lab) =>
    sessionRequests.some(r => r.labName === lab.name) ||
    existingRequests.some(r => r.lab === lab.name);

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        bgcolor: t.surface,
        border: `1px solid ${t.borderSubtle}`,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ p: { xs: 1.5, md: 2 }, borderBottom: `1px solid ${t.borderSubtle}` }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
          <TextField
            fullWidth
            size="small"
            value={labSearch}
            onChange={(e) => setLabSearch(e.target.value)}
            placeholder="Search lab tests…"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: t.textMuted, fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1.5, bgcolor: t.fieldBg,
                '& fieldset': { borderColor: t.borderSubtle },
              },
            }}
          />
          <Chip
            icon={<CalendarIcon sx={{ fontSize: 16 }} />}
            label={moment(today).format('ddd, MMM D, YYYY')}
            sx={{
              height: 36, px: 1, borderRadius: 1.5, fontWeight: 700,
              bgcolor: alpha(t.accent.teal, 0.12),
              color: t.accent.teal,
              border: `1px solid ${alpha(t.accent.teal, 0.32)}`,
              '& .MuiChip-icon': { color: t.accent.teal },
            }}
          />
        </Stack>
      </Box>

      {labsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress size={26} sx={{ color: t.accent.teal }} />
        </Box>
      ) : labs.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <ScienceIcon sx={{ fontSize: 44, color: t.textDim, mb: 1 }} />
          <Typography sx={{ color: t.textMuted, fontWeight: 600 }}>
            {labSearch ? 'No labs match that search.' : 'No lab tests configured for this patient\'s insurance.'}
          </Typography>
        </Box>
      ) : (
        <TableContainer sx={{ maxHeight: 560 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {['Lab Test', 'Code', 'Amount', 'Status', ''].map((h, i) => (
                  <TableCell
                    key={h + i}
                    align={i >= 2 ? (i === 4 ? 'center' : 'right') : 'left'}
                    sx={{
                      bgcolor: t.surfaceAlt,
                      color: t.textMuted,
                      borderBottom: `1px solid ${t.borderSubtle}`,
                      fontSize: '0.72rem',
                      fontWeight: 800,
                      textTransform: 'uppercase',
                      letterSpacing: '0.6px',
                    }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {labs.map((lab, idx) => {
                const key  = `${lab.code || 'L'}-${idx}`;
                const done = isRequested(lab);
                const busy = requestingLabId === key;
                return (
                  <TableRow
                    key={key}
                    sx={{
                      '&:hover': { bgcolor: t.rowHover },
                      ...(done && { bgcolor: alpha(t.accent.emerald, 0.08) }),
                    }}
                  >
                    <TableCell sx={{ color: t.text, fontWeight: 600, borderColor: t.borderSubtle, textTransform: 'uppercase' }}>
                      {lab.name}
                    </TableCell>
                    <TableCell sx={{ color: t.textMuted, fontFamily: 'monospace', fontSize: '0.78rem', borderColor: t.borderSubtle }}>
                      {lab.code || '—'}
                    </TableCell>
                    <TableCell align="right" sx={{ color: t.text, fontWeight: 700, borderColor: t.borderSubtle }}>
                      {Number(lab.amount) === 0
                        ? <Chip size="small" label="Free" sx={chipSx(t, t.accent.emerald)} />
                        : formatWithOutSom(lab.amount)}
                    </TableCell>
                    <TableCell align="right" sx={{ borderColor: t.borderSubtle }}>
                      {done ? (
                        <Chip
                          icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                          size="small"
                          label="Requested"
                          sx={chipSx(t, t.accent.emerald)}
                        />
                      ) : Number(lab.amount) === 0 ? (
                        <Chip size="small" label="Insured" sx={chipSx(t, t.accent.blue)} />
                      ) : (
                        <Chip
                          icon={<HourglassIcon sx={{ fontSize: 14 }} />}
                          size="small"
                          label="Pay at Accounts"
                          sx={chipSx(t, t.accent.amber)}
                        />
                      )}
                    </TableCell>
                    <TableCell align="center" sx={{ borderColor: t.borderSubtle }}>
                      {busy ? (
                        <CircularProgress size={20} sx={{ color: t.accent.teal }} />
                      ) : (
                        <Tooltip title={done ? 'Request again' : 'Request'}>
                          <IconButton
                            size="small"
                            onClick={() => onRequest(lab, key)}
                            sx={{
                              bgcolor: alpha(t.accent.teal, 0.12),
                              color: t.accent.teal,
                              '&:hover': { bgcolor: alpha(t.accent.teal, 0.22) },
                            }}
                          >
                            <SendIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}

// ──────────────────────────────────────────────────────────────────────
//  Right-rail summary — session + existing requests + payment note
// ──────────────────────────────────────────────────────────────────────
function RequestSidebar({ t, sessionRequests, existingRequests, currentUser }) {
  return (
    <Stack spacing={2}>
      <Paper
        elevation={0}
        sx={{
          p: 1.75, borderRadius: 2,
          bgcolor: t.surface, border: `1px solid ${t.borderSubtle}`,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
          <InfoIcon sx={{ color: t.accent.teal, fontSize: 18 }} />
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: t.text, letterSpacing: '0.4px', textTransform: 'uppercase' }}>
            How this works
          </Typography>
        </Stack>
        <Typography sx={{ fontSize: '0.78rem', color: t.textMuted, lineHeight: 1.55 }}>
          Paid labs are routed to <strong style={{ color: t.text }}>Accounts</strong> for settlement
          first — the patient only appears in <strong style={{ color: t.text }}>My Patients</strong>{' '}
          after payment is processed. Insured/free tests skip that step and are added to the worklist
          immediately.
        </Typography>
        {currentUser && (
          <Typography sx={{ fontSize: '0.72rem', color: t.textDim, mt: 1.25 }}>
            Requesting as <strong style={{ color: t.textMuted }}>{currentUser}</strong>
          </Typography>
        )}
      </Paper>

      <Paper
        elevation={0}
        sx={{
          p: 1.75, borderRadius: 2,
          bgcolor: t.surface, border: `1px solid ${t.borderSubtle}`,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.25 }}>
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: t.text, letterSpacing: '0.4px', textTransform: 'uppercase' }}>
            Requested this session
          </Typography>
          <Chip
            size="small"
            label={sessionRequests.length}
            sx={chipSx(t, t.accent.teal)}
          />
        </Stack>
        {sessionRequests.length === 0 ? (
          <Typography sx={{ fontSize: '0.78rem', color: t.textDim }}>
            Nothing requested yet. Click <SendIcon sx={{ fontSize: 14, verticalAlign: 'middle' }} /> on a lab to start.
          </Typography>
        ) : (
          <Stack divider={<Divider sx={{ borderColor: t.borderSubtle }} />} spacing={0.75}>
            {sessionRequests.map((r, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{
                    fontSize: '0.82rem', color: t.text, fontWeight: 600,
                    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>
                    {r.labName}
                  </Typography>
                  {r.reqNo && (
                    <Typography sx={{
                      fontSize: '0.7rem', fontFamily: 'monospace',
                      color: t.accent.teal, fontWeight: 700, mt: 0.25,
                    }}>
                      {r.reqNo}
                    </Typography>
                  )}
                  <Typography sx={{ fontSize: '0.7rem', color: t.textMuted }}>
                    {moment(r.at).format('HH:mm')} · {r.insured ? 'Insured' : formatWithOutSom(r.amount)}
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  label={r.insured ? 'Worklist' : 'Pending'}
                  sx={chipSx(t, r.insured ? t.accent.emerald : t.accent.amber)}
                />
              </Box>
            ))}
          </Stack>
        )}
      </Paper>

      {existingRequests.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            p: 1.75, borderRadius: 2,
            bgcolor: t.surface, border: `1px solid ${t.borderSubtle}`,
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.25 }}>
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: t.text, letterSpacing: '0.4px', textTransform: 'uppercase' }}>
              Already on file today
            </Typography>
            <Chip
              size="small"
              icon={<HospitalIcon sx={{ fontSize: 14 }} />}
              label={existingRequests.length}
              sx={chipSx(t, t.accent.blue)}
            />
          </Stack>
          <Stack spacing={0.5}>
            {existingRequests.slice(0, 8).map((r, i) => (
              <Typography
                key={i}
                sx={{
                  fontSize: '0.76rem', color: t.textMuted,
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}
              >
                · {r.lab}
              </Typography>
            ))}
            {existingRequests.length > 8 && (
              <Typography sx={{ fontSize: '0.72rem', color: t.textDim }}>
                …and {existingRequests.length - 8} more
              </Typography>
            )}
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}
